'use client';
import React, { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { Elements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Home, Building2, Check, Upload, X, Calendar, Clock, FileText, Camera, MapPin, Plus, AlertCircle, CreditCard } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import { getStripe } from '@/lib/stripe/client';
import CheckoutForm from './CheckoutForm';
import type { PaymentIntent } from '@stripe/stripe-js';

type PropertyType = 'apartment' | 'house' | 'condo' | 'office';
type ServiceType = 'standard' | 'deep' | 'movein' | 'moveout';

interface SavedAddress {
  id: string;
  label: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postcode: string;
  is_default: boolean;
}

interface BookingFormData {
  propertyType: PropertyType;
  bedrooms: number;
  bathrooms: number;
  serviceType: ServiceType;
  addons: string[];
  date: string;
  time: string;
  notes: string;
}

interface RoomPhotoSlot {
  roomLabel: string;
  photos: File[];
}

interface ServicePrice {
  id: string;
  service_key: string;
  label: string;
  base_price: number;
  description: string;
  duration: string;
  is_popular: boolean;
}

interface AddonService {
  id: string;
  addon_key: string;
  label: string;
  price: number;
  is_active: boolean;
}

const STEP_LABELS = ['Property', 'Address', 'Rooms', 'Service', 'Schedule', 'Details', 'Payment'];
const STORAGE_KEY = 'cleanflow_addresses';

const propertyTypes = [
  { id: 'apartment' as PropertyType, label: 'Apartment', icon: Building2, desc: 'Studio to multi-bedroom' },
  { id: 'house' as PropertyType, label: 'House', icon: Home, desc: 'Single or multi-story' },
  { id: 'condo' as PropertyType, label: 'Condo', icon: Building2, desc: 'Condo or townhouse' },
  { id: 'office' as PropertyType, label: 'Office', icon: Building2, desc: 'Commercial space' },
];

// Fallback defaults in case DB is unavailable
const DEFAULT_SERVICES: ServicePrice[] = [
  { id: 'standard', service_key: 'standard', label: 'Standard Clean', base_price: 89, description: 'Regular maintenance cleaning for well-kept homes', duration: '2–3 hrs', is_popular: false },
  { id: 'deep', service_key: 'deep', label: 'Deep Clean', base_price: 159, description: 'Thorough top-to-bottom clean including appliances', duration: '4–6 hrs', is_popular: true },
  { id: 'movein', service_key: 'movein', label: 'Move In', base_price: 219, description: 'Detailed clean for moving into a new home', duration: '5–8 hrs', is_popular: false },
  { id: 'moveout', service_key: 'moveout', label: 'Move Out', base_price: 219, description: 'Deposit-ready clean for vacating a property', duration: '5–8 hrs', is_popular: false },
];

const DEFAULT_ADDONS: AddonService[] = [
  { id: 'inside-fridge', addon_key: 'inside-fridge', label: 'Inside Fridge', price: 25, is_active: true },
  { id: 'inside-oven', addon_key: 'inside-oven', label: 'Inside Oven', price: 25, is_active: true },
  { id: 'windows', addon_key: 'windows', label: 'Interior Windows', price: 35, is_active: true },
  { id: 'laundry', addon_key: 'laundry', label: 'Laundry (1 load)', price: 20, is_active: true },
  { id: 'garage', addon_key: 'garage', label: 'Garage Sweep', price: 30, is_active: true },
  { id: 'balcony', addon_key: 'balcony', label: 'Balcony / Patio', price: 20, is_active: true },
];

const availableTimes = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

function calculatePrice(service: ServiceType | undefined, beds: number, baths: number, selectedAddons: string[], serviceList: ServicePrice[], addonList: AddonService[]): number {
  if (!service) return 0;
  const base = serviceList.find(s => s.service_key === service)?.base_price || 0;
  const roomMod = (beds - 2) * 15 + (baths - 1) * 20;
  const addonTotal = selectedAddons.reduce((sum, key) => {
    const a = addonList.find(a => a.addon_key === key);
    return sum + (a?.price || 0);
  }, 0);
  return Math.max(base + roomMod + addonTotal, base);
}

function loadSavedAddresses(): SavedAddress[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const emptyAddressForm = () => ({
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
});

export default function BookingWizard() {
  const supabase = createClient();
  const [step, setStep] = useState(0);
  const [propertyType, setPropertyType] = useState<PropertyType | null>(null);

  // Address step state
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddressForm, setNewAddressForm] = useState(emptyAddressForm());
  const [addressFormError, setAddressFormError] = useState<string | null>(null);

  const [bedrooms, setBedrooms] = useState(2);
  const [bathrooms, setBathrooms] = useState(1);
  const [kitchens, setKitchens] = useState(1);
  const [serviceType, setServiceType] = useState<ServiceType | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  // Per-room photo slots
  const [roomPhotoSlots, setRoomPhotoSlots] = useState<RoomPhotoSlot[]>([]);
  // Additional optional photos
  const [additionalPhotos, setAdditionalPhotos] = useState<File[]>([]);
  const [isDraggingAdditional, setIsDraggingAdditional] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [serviceList, setServiceList] = useState<ServicePrice[]>(DEFAULT_SERVICES);
  const [addonList, setAddonList] = useState<AddonService[]>(DEFAULT_ADDONS);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // Customer info for payment
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  // Stripe payment state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [jobRef, setJobRef] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const stripePromise = getStripe();

  useEffect(() => {
    setSavedAddresses(loadSavedAddresses());
  }, []);

  // When entering address step, pre-select default address if available
  useEffect(() => {
    if (step === 1) {
      const addresses = loadSavedAddresses();
      setSavedAddresses(addresses);
      if (addresses.length > 0 && !selectedAddressId) {
        const def = addresses.find(a => a.is_default) || addresses[0];
        setSelectedAddressId(def.id);
        setShowNewAddressForm(false);
      } else if (addresses.length === 0) {
        setShowNewAddressForm(true);
      }
    }
  }, [step]);

  // Build room slots whenever room counts change and we're on step 5
  useEffect(() => {
    const slots: RoomPhotoSlot[] = [];
    for (let i = 1; i <= bedrooms; i++) slots.push({ roomLabel: `Bedroom ${i}`, photos: [] });
    for (let i = 1; i <= bathrooms; i++) slots.push({ roomLabel: `Bathroom ${i}`, photos: [] });
    for (let i = 1; i <= kitchens; i++) slots.push({ roomLabel: `Kitchen${kitchens > 1 ? ` ${i}` : ''}`, photos: [] });
    setRoomPhotoSlots(slots);
  }, [bedrooms, bathrooms, kitchens]);

  const fetchPricing = useCallback(async () => {
    try {
      const [svcResult, addonResult] = await Promise.all([
        supabase.from('service_prices').select('*').eq('is_active', true).order('created_at'),
        supabase.from('addon_services').select('*').eq('is_active', true).order('sort_order'),
      ]);
      const svcData = svcResult.data;
      const addonData = addonResult.data;
      if (svcData && svcData.length > 0) setServiceList(svcData);
      if (addonData && addonData.length > 0) setAddonList(addonData);
    } catch {
      // silently fall back to defaults
    } finally {
      setPricingLoaded(true);
    }
  }, [supabase]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const price = calculatePrice(serviceType || undefined, bedrooms, bathrooms, selectedAddons, serviceList, addonList);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAdditional(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setAdditionalPhotos(prev => [...prev, ...files].slice(0, 10));
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setAdditionalPhotos(prev => [...prev, ...files].slice(0, 10));
  };

  const handleRoomFileInput = (slotIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setRoomPhotoSlots(prev => prev.map((slot, i) =>
      i === slotIndex ? { ...slot, photos: [...slot.photos, ...files].slice(0, 5) } : slot
    ));
  };

  const handleRoomDrop = (slotIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setRoomPhotoSlots(prev => prev.map((slot, i) =>
      i === slotIndex ? { ...slot, photos: [...slot.photos, ...files].slice(0, 5) } : slot
    ));
  };

  const removeRoomPhoto = (slotIndex: number, photoIndex: number) => {
    setRoomPhotoSlots(prev => prev.map((slot, i) =>
      i === slotIndex ? { ...slot, photos: slot.photos.filter((_, pi) => pi !== photoIndex) } : slot
    ));
  };

  const handleAdditionalDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAdditional(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    setAdditionalPhotos(prev => [...prev, ...files].slice(0, 10));
  }, []);

  const handleAdditionalFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    setAdditionalPhotos(prev => [...prev, ...files].slice(0, 10));
  };

  const toggleAddon = (key: string) => {
    setSelectedAddons(prev => prev.includes(key) ? prev.filter(a => a !== key) : [...prev, key]);
  };

  const handleNewAddressSubmit = () => {
    if (!newAddressForm.address_line1.trim() || !newAddressForm.city.trim() || !newAddressForm.postcode.trim()) {
      setAddressFormError('Please fill in Address Line 1, City, and Postcode.');
      return false;
    }
    setAddressFormError(null);
    return true;
  };

  const canProceed = () => {
    if (step === 0) return propertyType !== null;
    if (step === 1) {
      if (showNewAddressForm) {
        return newAddressForm.address_line1.trim() !== '' && newAddressForm.city.trim() !== '' && newAddressForm.postcode.trim() !== '';
      }
      return selectedAddressId !== null;
    }
    if (step === 2) return bedrooms >= 1 && bathrooms >= 1;
    if (step === 3) return serviceType !== null;
    if (step === 4) return selectedDate !== '' && selectedTime !== '';
    if (step === 5) return roomPhotoSlots.every(slot => slot.photos.length > 0);
    if (step === 6) return customerFirstName.trim() !== '' && customerLastName.trim() !== '' && customerEmail.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);
    return true;
  };

  const getSelectedAddressLabel = () => {
    if (showNewAddressForm && newAddressForm.address_line1) {
      return `${newAddressForm.address_line1}, ${newAddressForm.city}`;
    }
    if (selectedAddressId) {
      const addr = savedAddresses.find(a => a.id === selectedAddressId);
      if (addr) return `${addr.address_line1}, ${addr.city}`;
    }
    return '—';
  };

  const getSelectedAddressPostcode = () => {
    if (showNewAddressForm) return newAddressForm.postcode;
    if (selectedAddressId) {
      const addr = savedAddresses.find(a => a.id === selectedAddressId);
      return addr?.postcode || '';
    }
    return '';
  };

  const getSelectedAddressCity = () => {
    if (showNewAddressForm) return newAddressForm.city;
    if (selectedAddressId) {
      const addr = savedAddresses.find(a => a.id === selectedAddressId);
      return addr?.city || '';
    }
    return '';
  };

  // Initiate Stripe payment intent when reaching payment step
  const initiatePayment = async () => {
    if (clientSecret) return; // already created
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const { data, error } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
        jobRef: string;
      }>('create-payment-intent', {
        body: {
          jobData: {
            amount: price,
            currency: 'gbp',
            service: serviceList.find(s => s.service_key === serviceType)?.label || serviceType || '',
            address: getSelectedAddressLabel(),
            scheduledDate: selectedDate,
            scheduledTime: selectedTime,
            propertyType: propertyType || '',
            bedrooms,
            bathrooms,
            notes: notes || undefined,
          },
          customerInfo: {
            userId: null,
            firstName: customerFirstName,
            lastName: customerLastName,
            email: customerEmail,
            stripeCustomerId: null,
            addressLine1: showNewAddressForm ? newAddressForm.address_line1 : (savedAddresses.find(a => a.id === selectedAddressId)?.address_line1 || ''),
            city: getSelectedAddressCity(),
            postcode: getSelectedAddressPostcode(),
          },
        },
      });

      if (error) {
        const msg = (data as { error?: string })?.error ?? error.message ?? 'Failed to initialise payment';
        setPaymentError(msg);
        return;
      }

      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId);
        setJobRef(data.jobRef);
      }
    } catch (err: unknown) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to initialise payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent: PaymentIntent) => {
    setSubmitted(true);
    toast.success('Payment successful! Your booking is confirmed.');
  };

  const handlePaymentError = (message: string) => {
    setPaymentError(message);
    toast.error(message);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full gradient-primary flex items-center justify-center mx-auto">
            <Check size={36} className="text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-foreground">Booking Confirmed!</h1>
          <p className="text-muted-foreground">Payment received. Your cleaning has been scheduled. You&apos;ll receive a confirmation email and we&apos;ll notify you when a cleaner is assigned.</p>
          <div className="bg-card rounded-2xl border border-border p-5 text-left space-y-3 card-shadow">
            {jobRef && (
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Booking Ref</span><span className="font-bold text-primary">{jobRef}</span></div>
            )}
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Service</span><span className="font-semibold">{serviceList.find(s => s.service_key === serviceType)?.label}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Date & Time</span><span className="font-semibold">{selectedDate} · {selectedTime}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Property</span><span className="font-semibold capitalize">{propertyType} · {bedrooms}BR / {bathrooms}BA / {kitchens}KT</span></div>
            <div className="flex justify-between text-sm border-t border-border pt-3"><span className="font-bold text-foreground">Paid</span><span className="font-bold text-lg text-primary font-tabular">£{price}</span></div>
          </div>
          <div className="flex gap-3">
            <Link href="/customer-dashboard" className="flex-1 py-3 rounded-xl gradient-primary text-white font-semibold text-sm text-center hover:opacity-90 active:scale-95 transition-all">View Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header - logo only, no step indicator */}
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-base text-foreground">EzCleanCare</span>
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_260px] gap-6">
          {/* Left: Vertical Step Sidebar */}
          <div className="hidden lg:block">
            <div className="sticky top-20 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">Booking Steps</p>
              {STEP_LABELS.map((label, i) => {
                const isCompleted = i < step;
                const isCurrent = i === step;
                const isPending = i > step;
                return (
                  <div key={`sidebar-step-${i}`} className="relative">
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${isCurrent ? 'bg-primary/10' : isCompleted ? 'bg-transparent' : 'bg-transparent'}`}>
                      {/* Step number / check */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold transition-all ${isCompleted ? 'bg-primary text-white' : isCurrent ? 'bg-primary text-white ring-4 ring-primary/20' : 'bg-muted text-muted-foreground'}`}>
                        {isCompleted ? <Check size={14} /> : i + 1}
                      </div>
                      {/* Label */}
                      <span className={`text-sm font-medium transition-colors ${isCurrent ? 'text-primary font-semibold' : isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {label}
                      </span>
                    </div>
                    {/* Connector line */}
                    {i < STEP_LABELS.length - 1 && (
                      <div className="ml-7 w-0.5 h-3 bg-border mx-auto" style={{ marginLeft: '1.75rem' }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Main form */}
          <div className="lg:col-span-1">
            {/* Step 0: Property Type */}
            {step === 0 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">What type of property?</h2>
                  <p className="text-sm text-muted-foreground">Select the property type you need cleaned.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {propertyTypes.map((pt) => (
                    <button
                      key={`pt-${pt.id}`}
                      onClick={() => setPropertyType(pt.id)}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-150 hover:border-primary/50 ${propertyType === pt.id ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${propertyType === pt.id ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                        <pt.icon size={20} />
                      </div>
                      <p className="text-sm font-bold text-foreground">{pt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{pt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Address Selection */}
            {step === 1 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">Where should we clean?</h2>
                  <p className="text-sm text-muted-foreground">Choose a saved address or enter a new one.</p>
                </div>

                {/* Saved addresses */}
                {savedAddresses.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Saved Addresses</p>
                    {savedAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        onClick={() => {
                          setSelectedAddressId(addr.id);
                          setShowNewAddressForm(false);
                          setAddressFormError(null);
                        }}
                        className={`w-full p-4 rounded-2xl border-2 text-left transition-all duration-150 hover:border-primary/50 ${selectedAddressId === addr.id && !showNewAddressForm ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedAddressId === addr.id && !showNewAddressForm ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                            <MapPin size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-bold text-foreground truncate">{addr.label || addr.address_line1}</p>
                              {addr.is_default && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">Default</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                            <p className="text-xs text-muted-foreground">{addr.city}, {addr.postcode}</p>
                          </div>
                          {selectedAddressId === addr.id && !showNewAddressForm && (
                            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check size={11} className="text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Enter new address toggle */}
                {!showNewAddressForm ? (
                  <button
                    onClick={() => {
                      setShowNewAddressForm(true);
                      setSelectedAddressId(null);
                      setNewAddressForm(emptyAddressForm());
                      setAddressFormError(null);
                    }}
                    className="w-full p-4 rounded-2xl border-2 border-dashed border-border bg-card text-left transition-all duration-150 hover:border-primary/50 hover:bg-primary/5 flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                      <Plus size={16} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Enter a new address</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Manually type in the address for this booking</p>
                    </div>
                  </button>
                ) : (
                  <div className="bg-card rounded-2xl border-2 border-primary/30 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-foreground">New Address</p>
                      {savedAddresses.length > 0 && (
                        <button
                          onClick={() => {
                            setShowNewAddressForm(false);
                            const def = savedAddresses.find(a => a.is_default) || savedAddresses[0];
                            setSelectedAddressId(def.id);
                            setAddressFormError(null);
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>

                    {addressFormError && (
                      <div className="bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-xl px-3 py-2">
                        {addressFormError}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                        Address Line 1 <span className="text-destructive">*</span>
                      </label>
                      <input
                        value={newAddressForm.address_line1}
                        onChange={e => setNewAddressForm(prev => ({ ...prev, address_line1: e.target.value }))}
                        placeholder="House number and street name"
                        className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                        Address Line 2 <span className="text-muted-foreground/60">(optional)</span>
                      </label>
                      <input
                        value={newAddressForm.address_line2}
                        onChange={e => setNewAddressForm(prev => ({ ...prev, address_line2: e.target.value }))}
                        placeholder="Flat, apartment, suite, etc."
                        className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                          City <span className="text-destructive">*</span>
                        </label>
                        <input
                          value={newAddressForm.city}
                          onChange={e => setNewAddressForm(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="e.g. London"
                          className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                          Postcode <span className="text-destructive">*</span>
                        </label>
                        <input
                          value={newAddressForm.postcode}
                          onChange={e => setNewAddressForm(prev => ({ ...prev, postcode: e.target.value }))}
                          placeholder="e.g. SW1A 1AA"
                          className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Rooms */}
            {step === 2 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">How many rooms?</h2>
                  <p className="text-sm text-muted-foreground">Room count affects the price and time estimate.</p>
                </div>
                {[
                  { label: 'Bedrooms', value: bedrooms, setter: setBedrooms, min: 0, max: 8 },
                  { label: 'Bathrooms', value: bathrooms, setter: setBathrooms, min: 1, max: 6 },
                  { label: 'Kitchens', value: kitchens, setter: setKitchens, min: 1, max: 4 },
                ].map(({ label, value, setter, min, max }) => (
                  <div key={`room-${label}`} className="bg-card rounded-2xl border border-border p-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Select number of {label.toLowerCase()}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setter(Math.max(min, value - 1))}
                        className="w-9 h-9 rounded-xl border border-border bg-muted flex items-center justify-center text-foreground font-bold hover:bg-border transition-colors disabled:opacity-40"
                        disabled={value <= min}
                      >
                        −
                      </button>
                      <span className="text-2xl font-extrabold text-foreground w-8 text-center font-tabular">{value}</span>
                      <button
                        onClick={() => setter(Math.min(max, value + 1))}
                        className="w-9 h-9 rounded-xl border border-border bg-muted flex items-center justify-center text-foreground font-bold hover:bg-border transition-colors disabled:opacity-40"
                        disabled={value >= max}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Service Type */}
            {step === 3 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">Choose your service</h2>
                  <p className="text-sm text-muted-foreground">Select the cleaning type that fits your needs.</p>
                </div>
                <div className="space-y-3">
                  {serviceList.map((svc) => (
                    <button
                      key={`svc-${svc.service_key}`}
                      onClick={() => setServiceType(svc.service_key as ServiceType)}
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all duration-150 hover:border-primary/50 relative ${serviceType === svc.service_key ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${serviceType === svc.service_key ? 'border-primary bg-primary' : 'border-border'}`}>
                          {serviceType === svc.service_key && <Check size={11} className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1 gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-foreground">{svc.label}</p>
                              {svc.is_popular && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary text-white flex-shrink-0">Popular</span>
                              )}
                            </div>
                            <p className="text-sm font-bold text-primary font-tabular">From £{svc.base_price}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">{svc.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">Est. {svc.duration}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {addonList.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-foreground mb-3">Add-on Services</p>
                    <div className="grid grid-cols-2 gap-2">
                      {addonList.map((addon) => (
                        <button
                          key={`addon-${addon.addon_key}`}
                          onClick={() => toggleAddon(addon.addon_key)}
                          className={`p-3 rounded-xl border text-left text-sm transition-all duration-150 ${selectedAddons.includes(addon.addon_key) ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-card text-foreground hover:border-primary/40'}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{addon.label}</span>
                            <span className="font-bold font-tabular">+£{addon.price}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Schedule */}
            {step === 4 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">When should we come?</h2>
                  <p className="text-sm text-muted-foreground">Select your preferred date and arrival window.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    <Calendar size={14} className="inline mr-1.5" />
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    <Clock size={14} className="inline mr-1.5" />
                    Preferred Arrival Time
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimes.map((t) => (
                      <button
                        key={`time-${t}`}
                        onClick={() => setSelectedTime(t)}
                        className={`py-2.5 rounded-xl border text-sm font-medium transition-all duration-150 ${selectedTime === t ? 'border-primary bg-primary text-white' : 'border-border bg-card text-foreground hover:border-primary/40'}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Notes + Photos */}
            {step === 5 && (
              <div className="animate-slide-up space-y-6">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">Final details</h2>
                  <p className="text-sm text-muted-foreground">Add any instructions and upload photos to help your cleaner.</p>
                </div>

                {/* Special Instructions */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    <FileText size={14} className="inline mr-1.5" />
                    Special Instructions
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">Entry code, focus areas, pets, allergies, or anything else your cleaner should know.</p>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={4}
                    placeholder="e.g. Front door code is 1234. Please pay extra attention to the kitchen. We have a cat — she's friendly!"
                    className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all resize-none"
                  />
                </div>

                {/* Per-Room Photo Upload */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Camera size={14} className="text-foreground" />
                    <label className="text-sm font-semibold text-foreground">Room Photos</label>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Required</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">Upload at least one photo of each room so your cleaner knows what to expect.</p>

                  <div className="space-y-4">
                    {roomPhotoSlots.map((slot, slotIndex) => {
                      const hasPhoto = slot.photos.length > 0;
                      return (
                        <div
                          key={`room-slot-${slotIndex}`}
                          className={`rounded-2xl border-2 p-4 transition-all ${hasPhoto ? 'border-success/40 bg-success/5' : 'border-border bg-card'}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${hasPhoto ? 'bg-success/20' : 'bg-muted'}`}>
                                <Camera size={13} className={hasPhoto ? 'text-success' : 'text-muted-foreground'} />
                              </div>
                              <span className="text-sm font-bold text-foreground">{slot.roomLabel}</span>
                            </div>
                            {!hasPhoto && (
                              <div className="flex items-center gap-1 text-xs text-amber-600">
                                <AlertCircle size={12} />
                                <span>Required</span>
                              </div>
                            )}
                            {hasPhoto && (
                              <span className="text-xs font-semibold text-success">{slot.photos.length} photo{slot.photos.length > 1 ? 's' : ''}</span>
                            )}
                          </div>

                          {/* Uploaded thumbnails */}
                          {slot.photos.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {slot.photos.map((photo, photoIndex) => (
                                <div key={`rp-${slotIndex}-${photoIndex}`} className="relative w-14 h-14 rounded-xl overflow-hidden bg-muted border border-border group">
                                  <img src={URL.createObjectURL(photo)} alt={`${slot.roomLabel} photo ${photoIndex + 1}`} className="w-full h-full object-cover" />
                                  <button
                                    onClick={() => removeRoomPhoto(slotIndex, photoIndex)}
                                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-foreground/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Remove photo"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Drop zone */}
                          <div
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => handleRoomDrop(slotIndex, e)}
                            className="border border-dashed border-border rounded-xl p-3 text-center hover:border-primary/40 transition-colors"
                          >
                            <label className="cursor-pointer flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                              <Upload size={13} />
                              <span>Click to upload or drag & drop — up to 5 photos</span>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={e => handleRoomFileInput(slotIndex, e)}
                                capture="environment"
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Additional Photos (Optional) */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Camera size={14} className="text-foreground" />
                    <label className="text-sm font-semibold text-foreground">Additional Photos</label>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Optional</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Upload photos of specific items that need attention — walls, floors, appliances, upholstery, etc.</p>

                  <div
                    onDragOver={e => { e.preventDefault(); setIsDraggingAdditional(true); }}
                    onDragLeave={() => setIsDraggingAdditional(false)}
                    onDrop={handleAdditionalDrop}
                    className={`border-2 border-dashed rounded-2xl p-5 text-center transition-all duration-150 ${isDraggingAdditional ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  >
                    <Upload size={22} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground mb-1">Drag & drop photos here</p>
                    <p className="text-xs text-muted-foreground mb-3">Walls, floors, appliances, upholstery — up to 10 photos, JPG/PNG</p>
                    <label className="cursor-pointer px-4 py-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                      Browse Files
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleAdditionalFileInput} capture="environment" />
                    </label>
                  </div>

                  {additionalPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {additionalPhotos.map((photo, i) => (
                        <div key={`addl-${i}-${photo.name}`} className="relative w-16 h-16 rounded-xl overflow-hidden bg-muted border border-border group">
                          <img src={URL.createObjectURL(photo)} alt={`Additional photo ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => setAdditionalPhotos(prev => prev.filter((_, idx) => idx !== i))}
                            className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-foreground/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove photo"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 6: Customer Info */}
            {step === 6 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">Your details</h2>
                  <p className="text-sm text-muted-foreground">We need your contact info to confirm the booking and send receipts.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">First Name <span className="text-destructive">*</span></label>
                    <input
                      value={customerFirstName}
                      onChange={e => setCustomerFirstName(e.target.value)}
                      placeholder="Jane"
                      className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Last Name <span className="text-destructive">*</span></label>
                    <input
                      value={customerLastName}
                      onChange={e => setCustomerLastName(e.target.value)}
                      placeholder="Smith"
                      className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Email Address <span className="text-destructive">*</span></label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={e => setCustomerEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">Your booking confirmation and receipt will be sent here.</p>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={14} className="text-primary" />
                    <p className="text-sm font-bold text-foreground">Ready to pay</p>
                  </div>
                  <p className="text-xs text-muted-foreground">After confirming your details, you&apos;ll be taken to the secure payment step to complete your booking of <span className="font-bold text-foreground">£{price}</span>.</p>
                </div>
              </div>
            )}

            {/* Step 7: Payment */}
            {step === 7 && (
              <div className="animate-slide-up space-y-5">
                <div>
                  <h2 className="text-xl font-extrabold text-foreground mb-1">Secure Payment</h2>
                  <p className="text-sm text-muted-foreground">Complete your booking by paying securely via Stripe.</p>
                </div>

                {paymentLoading && (
                  <div className="bg-card rounded-2xl border border-border p-8 text-center">
                    <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Setting up secure payment…</p>
                  </div>
                )}

                {paymentError && !paymentLoading && (
                  <div className="space-y-3">
                    <div className="bg-danger/10 border border-danger/20 rounded-xl px-4 py-3 text-sm text-danger">
                      {paymentError}
                    </div>
                    <button
                      onClick={() => { setClientSecret(null); setPaymentError(null); initiatePayment(); }}
                      className="w-full py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {clientSecret && !paymentLoading && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          borderRadius: '12px',
                          fontFamily: 'inherit',
                        },
                      },
                    }}
                  >
                    <CheckoutForm
                      clientSecret={clientSecret}
                      amount={price}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                )}
              </div>
            )}
          </div>

          {/* Price summary */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-2xl border border-border p-5 card-shadow sticky top-20 space-y-4">
              <h3 className="text-sm font-bold text-foreground">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Property</span>
                  <span className="font-medium capitalize">{propertyType || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground flex-shrink-0">Address</span>
                  <span className="font-medium text-right text-xs leading-tight">{getSelectedAddressLabel()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rooms</span>
                  <span className="font-medium">{bedrooms}BR / {bathrooms}BA / {kitchens}KT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium">{serviceType ? serviceList.find(s => s.service_key === serviceType)?.label : '—'}</span>
                </div>
                {selectedAddons.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Add-ons</span>
                    <span className="font-medium">{selectedAddons.length} selected</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{selectedDate || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">{selectedTime || '—'}</span>
                </div>
              </div>
              <div className="border-t border-border pt-3 flex justify-between items-baseline">
                <span className="text-sm font-bold text-foreground">Total</span>
                <span className="text-2xl font-extrabold text-primary font-tabular">{serviceType ? `£${price}` : '—'}</span>
              </div>
              <p className="text-xs text-muted-foreground">No hidden fees. Payment secured by Stripe.</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {step < 7 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 6 ? (
              <button
                onClick={() => {
                  if (step === 1 && showNewAddressForm) {
                    if (!handleNewAddressSubmit()) return;
                  }
                  setStep(s => s + 1);
                }}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={() => {
                  if (!canProceed()) return;
                  setStep(7);
                  initiatePayment();
                }}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CreditCard size={16} /> Proceed to Payment
              </button>
            )}
          </div>
        )}
        {step === 7 && (
          <div className="mt-8 pt-6 border-t border-border">
            <button
              onClick={() => setStep(6)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors active:scale-95"
            >
              <ArrowLeft size={16} /> Back to Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}